import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { ReactComponent as RefreshIcon } from '../../../assets/Refresh_icon.svg';
import { ReactComponent as GoogleDriveIcon } from '../../../assets/Google_Drive_icon.svg';
import { ReactComponent as AmazonS3Icon } from '../../../assets/amazon-s3-icon.svg';
import { ReactComponent as GoogleCloudIcon } from '../../../assets/google-cloud-icon.svg';
import { ReactComponent as AzureIcon } from '../../../assets/azure-icon.svg';
import './RSAAnalysis.css';
import TabsRSA from './Services/TabsRSA/TabsRSA';
import { API_BASE_URL, ENVIRONMENT, processApi } from '../../../API/api';
import PermissionGate from '../../PermissionGate/PermissionGate';
import AnalysisDashboard from '../AnalysisDashboard/AnalysisDashboard';
import '../AnalysisDashboard/AnalysisDashboard.css';
import { useUserRuns } from '../../../hooks/useUserRuns';
import progressService from '../../../services/progressService';
// Using relative URLs for production (CloudFront), API_BASE_URL for local development

// Add debugging to intercept all fetch/axios calls
(() => {
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    const url = args[0];
    if (typeof url === 'string' && url.includes('null')) {
      console.trace('🔍 NULL FETCH CALL DETECTED:', url);
      console.trace('🔍 Stack trace:', new Error().stack);
    }
    return originalFetch(...args);
  };
  
  const originalAxiosGet = axios.get;
  axios.get = (...args) => {
    const url = args[0];
    if (typeof url === 'string' && url.includes('null')) {
      console.trace('🔍 NULL AXIOS GET CALL DETECTED:', url);
      console.trace('🔍 Stack trace:', new Error().stack);
    }
    return originalAxiosGet(...args);
  };
})();

// --- Recursive tree node component ---
function FolderTreeNode({ folder, onSelect, selectedFolderId, parentPath = '', storageConnection, useSystemDrive }) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleExpand = async (e) => {
    // Prevent the click from triggering selection and form submission
    e.preventDefault();
    e.stopPropagation();
    if (!expanded) {
      setLoading(true);
      try {
        let url;
        if (useSystemDrive) {
          // Use system Google Drive
          url = ENVIRONMENT === 'local' 
            ? `${API_BASE_URL}/api/google-drive/folders?parent_id=${folder.id}`
            : `/api/google-drive/folders?parent_id=${folder.id}`;
        } else if (storageConnection) {
          // Use user's storage
          url = ENVIRONMENT === 'local' 
            ? `${API_BASE_URL}/api/storage/connections/${storageConnection.id}/folders?folder_id=${folder.id}`
            : `/api/storage/connections/${storageConnection.id}/folders?folder_id=${folder.id}`;
        }
        
        if (url) {
          console.log("🔧 RSAAnalysis - Making folder request to:", url);
          const response = await axios.get(url);
          setChildren(response.data.folders || []);
        } else {
          console.warn("No URL available for fetching folders");
          setChildren([]);
        }
      } catch (err) {
        console.error("❌ RSAAnalysis - Error fetching children for folder", folder.id, err);
        setChildren([]);
      } finally {
        setLoading(false);
      }
    }
    setExpanded(!expanded);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fullPath = parentPath ? `${parentPath}/${folder.name}` : folder.name;
    onSelect({ ...folder, fullPath });
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleExpand(e);
  };

  const isSelected = folder.id === selectedFolderId;

  return (
    <li className={`rsa-tree-node ${isSelected ? 'rsa-tree-node-selected' : ''}`}>
      <div 
        className="rsa-tree-node-content"
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <button 
          type="button"
          className={`rsa-tree-toggle ${expanded ? 'rsa-tree-toggle-expanded' : ''}`}
          onClick={toggleExpand}
          disabled={loading}
          aria-label={expanded ? 'Collapse folder' : 'Expand folder'}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="rsa-tree-icon">
          {expanded ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
        
        <span className="rsa-tree-label">{folder.name}</span>
        
        {loading && (
          <span className="rsa-tree-loading">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="rsa-tree-loading-spinner">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
              </circle>
            </svg>
          </span>
        )}
      </div>
      
        {expanded && children.length > 0 && (
          <ul className="rsa-tree-children">
            {children.map(child => (
              <FolderTreeNode
                key={child.id}
                folder={child}
                onSelect={onSelect}
                selectedFolderId={selectedFolderId}
                parentPath={parentPath ? `${parentPath}/${folder.name}` : folder.name}
                storageConnection={storageConnection}
                useSystemDrive={useSystemDrive}
              />
            ))}
          </ul>
        )}
    </li>
  );
}

// --- Storage Connection Modal ---
function StorageConnectionModal({ onClose, provider, onConnectionSuccess }) {
  const [s3Form, setS3Form] = useState({
    connection_name: '',
    access_key_id: '',
    secret_access_key: '',
    bucket_name: '',
    region: 'us-east-1'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isOAuthProvider = ['google_drive', 'azure', 'gcp'].includes(provider);
  const isS3Provider = provider === 's3';

  const providerNames = {
    'google_drive': 'Google Drive',
    'azure': 'Microsoft Azure',
    'gcp': 'Google Cloud Storage',
    's3': 'AWS S3'
  };

  // Helper function to get token from sessionStorage (handles JSON stringified tokens)
  const getAuthToken = async () => {
    try {
      // First, try to get token from AWS Amplify Auth (primary method)
      try {
        const session = await Auth.currentSession();
        if (session && session.getIdToken()) {
          const idToken = session.getIdToken().getJwtToken();
          console.log('✅ [getAuthToken] Got token from Amplify Auth');
          return idToken;
        }
      } catch (amplifyError) {
        console.log('ℹ️ [getAuthToken] Amplify Auth not available, trying other methods:', amplifyError.message);
      }
      
      // Fallback: Check multiple possible keys in storage (in order of likelihood)
      let token = sessionStorage.getItem('bimLiveAuthToken'); // Primary key used by this app
      
      if (!token) {
        token = sessionStorage.getItem('token');
      }
      if (!token) {
        token = sessionStorage.getItem('cognito_token');
      }
      if (!token) {
        token = sessionStorage.getItem('idToken');
      }
      if (!token) {
        // Check localStorage as fallback
        token = localStorage.getItem('bimLiveAuthToken') || 
                localStorage.getItem('token') || 
                localStorage.getItem('cognito_token') || 
                localStorage.getItem('idToken');
      }
      
      // Check cookies as last resort
      if (!token) {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'cognito_token' || name === 'token') {
            token = value;
            break;
          }
        }
      }
      
      console.log('🔍 [getAuthToken] Raw token from storage:', token ? `${token.substring(0, 20)}...` : 'null');
      
      if (!token) {
        console.warn('⚠️ [getAuthToken] No token found in sessionStorage, localStorage, cookies, or Amplify');
        console.log('🔍 [getAuthToken] Available sessionStorage keys:', Object.keys(sessionStorage));
        return null;
      }
      
      // Try to parse as JSON (token might be stored as JSON string)
      try {
        const parsed = JSON.parse(token);
        console.log('✅ [getAuthToken] Token is JSON, parsed structure:', typeof parsed, parsed ? Object.keys(parsed) : 'null');
        
        if (parsed && typeof parsed === 'string') {
          // Token is a JSON stringified string
          console.log('✅ [getAuthToken] Using parsed string token');
          return parsed;
        } else if (parsed && parsed.token) {
          // Token is an object with a token property
          console.log('✅ [getAuthToken] Using parsed.token');
          return parsed.token;
        } else if (parsed && parsed.idToken) {
          // Token is an object with an idToken property (Cognito)
          console.log('✅ [getAuthToken] Using parsed.idToken');
          return parsed.idToken;
        } else if (parsed && parsed.accessToken) {
          // Token is an object with an accessToken property
          console.log('✅ [getAuthToken] Using parsed.accessToken');
          return parsed.accessToken;
        } else {
          // If parsed is an object but we don't know the structure, log it
          console.warn('⚠️ [getAuthToken] Parsed token is object but no known property found:', parsed);
          // Try to return the whole object as string (some APIs accept this)
          return JSON.stringify(parsed);
        }
      } catch (parseError) {
        // Not JSON, use as-is (raw JWT token)
        console.log('✅ [getAuthToken] Token is not JSON, using as-is (raw JWT)');
        return token;
      }
    } catch (e) {
      console.error('❌ [getAuthToken] Error getting auth token:', e);
      return null;
    }
  };

  const handleOAuthConnect = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      // Get token (tries Amplify Auth first, then storage)
      const token = await getAuthToken();
      if (!token) {
        setError('You must be logged in to connect storage. Please log in and try again.');
        setLoading(false);
        return;
      }
      
      console.log('🔐 Starting OAuth flow for provider:', provider);
      
      // Store current page URL to redirect back after OAuth
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      // Add popup=true parameter to indicate this is a popup request
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/storage/oauth/${provider === 'google_drive' ? 'google' : provider}/authorize?return_url=${returnUrl}&popup=true`
        : `/api/storage/oauth/${provider === 'google_drive' ? 'google' : provider}/authorize?return_url=${returnUrl}&popup=true`;
      
      console.log('📡 Calling backend OAuth authorize endpoint:', url);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Backend response:', response.data);
      
      if (response.data && response.data.authorization_url) {
        const authUrl = response.data.authorization_url;
        console.log('🔗 Opening OAuth popup:', authUrl);
        
        // Open OAuth flow in a popup window
        const popupWidth = 600;
        const popupHeight = 700;
        const left = (window.screen.width - popupWidth) / 2;
        const top = (window.screen.height - popupHeight) / 2;
        
        const popup = window.open(
          authUrl,
          'oauth-popup',
          `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
        
        if (!popup) {
          setError('Popup blocked. Please allow popups for this site and try again.');
          setLoading(false);
          return;
        }
        
        // Listen for messages from the popup
        const handleMessage = (event) => {
          // Security: Only accept messages from our own origin
          if (event.origin !== window.location.origin) {
            console.warn('⚠️ Ignoring message from different origin:', event.origin);
            return;
          }
          
          if (event.data && event.data.type === 'oauth-callback') {
            console.log('✅ OAuth callback received:', event.data);
            
            if (event.data.success) {
              // Close popup
              popup.close();
              
              // Remove event listener
              window.removeEventListener('message', handleMessage);
              
              // Reload storage connections
              if (onConnectionSuccess) {
                onConnectionSuccess();
              }
              
              // Close modal and show success
              setLoading(false);
              onClose();
              
              // Show success message
              const providerNames = {
                'google_drive': 'Google Drive',
                'azure': 'Microsoft Azure',
                'gcp': 'Google Cloud Storage',
                's3': 'AWS S3'
              };
              const providerName = providerNames[provider] || provider;
              alert(`${providerName} connected successfully!`);
            } else {
              // Error occurred
              popup.close();
              window.removeEventListener('message', handleMessage);
              setError(event.data.error || 'Failed to connect storage. Please try again.');
              setLoading(false);
            }
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Check if popup was closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            setLoading(false);
            // Don't show error if user just closed the popup
          }
        }, 1000);
        
      } else {
        console.error('❌ No authorization_url in response:', response.data);
        setError('Failed to get authorization URL from server. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Error initiating OAuth:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      let errorMessage = 'Failed to connect. Please try again.';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in and try again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please check if Google OAuth is configured.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleS3Connect = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get token (tries Amplify Auth first, then storage)
      const token = await getAuthToken();
      if (!token) {
        setError('You must be logged in to connect storage. Please log in and try again.');
        setLoading(false);
        return;
      }
      
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/storage/connect/s3`
        : '/api/storage/connect/s3';
      
      const response = await axios.post(url, s3Form, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 201) {
        // Success - reload connections and close modal
        if (onConnectionSuccess) {
          onConnectionSuccess();
        }
        onClose();
      }
    } catch (err) {
      console.error('Error connecting S3:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to connect S3. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="TreeModalOverlay">
      <div className="TreeModalContainer">
        <div className="modal-header">
          <h2 className="modal-title">Connect {providerNames[provider] || provider}</h2>
        </div>
        
        <div className="modal-content">
          {isOAuthProvider && (
            <div className="modal-section">
              <p style={{ color: '#e2e8f0', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
                You'll be redirected to {providerNames[provider]} to grant permission for our application to access your storage.
                This is a secure OAuth 2.0 flow - you'll just need to click "Allow" on the authorization page.
              </p>
              <div style={{ 
                padding: '12px', 
                background: 'rgba(59, 131, 246, 0.1)', 
                borderRadius: '8px',
                border: '1px solid rgba(59, 131, 246, 0.3)',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#93c5fd', fontSize: '13px', margin: 0 }}>
                  <strong>What happens next:</strong>
                </p>
                <ul style={{ color: '#cbd5e1', fontSize: '13px', margin: '8px 0 0 20px', padding: 0 }}>
                  <li>You'll be redirected to {providerNames[provider]}'s login page</li>
                  <li>After logging in, you'll see a consent screen</li>
                  <li>Click "Allow" to grant access</li>
                  <li>You'll be redirected back automatically</li>
                </ul>
              </div>
            </div>
          )}

          {isS3Provider && (
            <form id="s3-connection-form" onSubmit={handleS3Connect}>
              <div className="modal-section">
                <label className="modal-label">Connection Name (optional)</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="My S3 Bucket"
                  value={s3Form.connection_name}
                  onChange={(e) => setS3Form({...s3Form, connection_name: e.target.value})}
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Access Key ID *</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  value={s3Form.access_key_id}
                  onChange={(e) => setS3Form({...s3Form, access_key_id: e.target.value})}
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Secret Access Key *</label>
                <input
                  type="password"
                  className="modal-input"
                  placeholder="Enter your secret key"
                  value={s3Form.secret_access_key}
                  onChange={(e) => setS3Form({...s3Form, secret_access_key: e.target.value})}
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Bucket Name *</label>
                <input
                  type="text"
                  className="modal-input"
                  placeholder="my-bucket-name"
                  value={s3Form.bucket_name}
                  onChange={(e) => setS3Form({...s3Form, bucket_name: e.target.value})}
                  required
                />
              </div>

              <div className="modal-section">
                <label className="modal-label">Region *</label>
                <select
                  className="modal-select"
                  value={s3Form.region}
                  onChange={(e) => setS3Form({...s3Form, region: e.target.value})}
                  required
                >
                  <option value="us-east-1">US East (N. Virginia) - us-east-1</option>
                  <option value="us-east-2">US East (Ohio) - us-east-2</option>
                  <option value="us-west-1">US West (N. California) - us-west-1</option>
                  <option value="us-west-2">US West (Oregon) - us-west-2</option>
                  <option value="eu-west-1">Europe (Ireland) - eu-west-1</option>
                  <option value="eu-central-1">Europe (Frankfurt) - eu-central-1</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore) - ap-southeast-1</option>
                  <option value="ap-southeast-2">Asia Pacific (Sydney) - ap-southeast-2</option>
                  <option value="ap-northeast-1">Asia Pacific (Tokyo) - ap-northeast-1</option>
                </select>
              </div>

              {error && (
                <div style={{
                  padding: '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: '#fca5a5',
                  fontSize: '13px',
                  marginBottom: '20px'
                }}>
                  {error}
                </div>
              )}
            </form>
          )}

          {error && isOAuthProvider && (
            <div style={{
              padding: '12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              color: '#fca5a5',
              fontSize: '13px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button 
            type="button"
            className="modal-btn modal-btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          {isOAuthProvider && (
            <button 
              type="button"
              className="modal-btn modal-btn-primary" 
              onClick={handleOAuthConnect}
              disabled={loading}
            >
              {loading ? 'Connecting...' : 'Connect & Authorize'}
            </button>
          )}
          {isS3Provider && (
            <button 
              type="submit"
              form="s3-connection-form"
              className="modal-btn modal-btn-primary" 
              disabled={loading || !s3Form.access_key_id || !s3Form.secret_access_key || !s3Form.bucket_name}
            >
              {loading ? 'Connecting...' : 'Connect S3'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Modal popup with the folder tree and shared drive selector ---
function FolderTreePopup({ onClose, onFolderSelect, onDriveSelect, storageConnection, useSystemDrive }) {
  // View: 'tree' = folder tree, 'selected_list' = list of selected folders
  const [view, setView] = useState('tree');
  // Accumulated selected folders (order of selection)
  const [selectedFoldersInModal, setSelectedFoldersInModal] = useState([]);
  // State for shared drives
  const [sharedDrives, setSharedDrives] = useState([]);
  const [selectedDrive, setSelectedDrive] = useState('');
  // Root folder id used to fetch folders (either shared drive id or manually entered)
  const [rootFolderId, setRootFolderId] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch available shared drives from backend
  const fetchSharedDrives = async () => { 
    try {
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/google-drive/shared-drives`
        : `/api/google-drive/shared-drives`;
      const response = await axios.get(url);
      // Handle both response formats and ensure we always have an array
      const drives = response.data?.drives || response.data?.shared_drives || [];
      const drivesArray = Array.isArray(drives) ? drives : [];
      setSharedDrives(drivesArray);
      // Update cache
      sessionStorage.setItem('sharedDrives', JSON.stringify(drivesArray));
    } catch (err) {
      console.error("Error fetching shared drives", err);
      // Set empty array on error to prevent component crash
      setSharedDrives([]);
    }
  };

  // Fetch subfolders for the current rootFolderId
  const fetchFolders = async (folderId) => {
    setLoading(true);
    try {
      if (useSystemDrive) {
        // Use system Google Drive
        console.log("🔧 RSAAnalysis - Fetching folders from system Google Drive, parent_id:", folderId);
        const url = ENVIRONMENT === 'local' 
          ? `${API_BASE_URL}/api/google-drive/folders?parent_id=${folderId}`
          : `/api/google-drive/folders?parent_id=${folderId}`;
        const response = await axios.get(url);
        console.log("🔧 RSAAnalysis - Folders response:", response.data);
        const folders = response.data?.folders || [];
        setFolders(Array.isArray(folders) ? folders : []);
        console.log(`✅ RSAAnalysis - Set ${folders.length} folders from system Google Drive`);
      } else if (storageConnection) {
        // Use user's storage connection
        console.log("🔧 RSAAnalysis - Fetching folders from user storage:", storageConnection.connection_name);
        const url = ENVIRONMENT === 'local' 
          ? `${API_BASE_URL}/api/storage/connections/${storageConnection.id}/folders?folder_id=${folderId}`
          : `/api/storage/connections/${storageConnection.id}/folders?folder_id=${folderId}`;
        const response = await axios.get(url);
        const folders = response.data?.folders || [];
        setFolders(Array.isArray(folders) ? folders : []);
        console.log(`✅ RSAAnalysis - Set ${folders.length} folders from ${storageConnection.connection_name}`);
      }
    } catch (err) {
      console.error("❌ RSAAnalysis - Error fetching folders for", folderId, err);
      setFolders([]);
    }
    setLoading(false);
  };

  // On mount, load shared drives from cache first, then fetch fresh data
  useEffect(() => {
    if (useSystemDrive) {
      // Use system Google Drive - load shared drives
      const cachedDrives = sessionStorage.getItem('sharedDrives');
      if (cachedDrives) {
        try {
          const parsedDrives = JSON.parse(cachedDrives);
          setSharedDrives(Array.isArray(parsedDrives) ? parsedDrives : []);
          console.log("✅ FolderTreePopup - Shared drives loaded from cache:", parsedDrives.length);
        } catch (e) {
          console.error("Error parsing cached shared drives:", e);
        }
      }
      fetchSharedDrives();
    } else if (storageConnection) {
      // Use user's storage - fetch root folders
      fetchFolders('root');
    }
  }, [useSystemDrive, storageConnection]);

  // When a shared drive is selected, update the rootFolderId and fetch its folders
  useEffect(() => {
    if (useSystemDrive && selectedDrive) {
      setRootFolderId(selectedDrive);
      fetchFolders(selectedDrive);
      // Get the drive name and pass it to parent
      const drive = sharedDrives.find(d => d.id === selectedDrive);
      if (drive && onDriveSelect) {
        onDriveSelect(drive.name);
      }
    }
  }, [selectedDrive, sharedDrives, onDriveSelect, useSystemDrive]);

  const handleSelect = (folder) => {
    setSelectedFolder(folder);
  };

  // Add current tree selection to list and switch to selected-list view
  const handleAddSelected = () => {
    if (selectedFolder) {
      setSelectedFoldersInModal(prev => [...prev, selectedFolder]);
      setSelectedFolder(null);
      setView('selected_list');
    } else {
      alert("Please select a folder from the tree.");
    }
  };

  const removeFolderAt = (index) => {
    setSelectedFoldersInModal(prev => prev.filter((_, i) => i !== index));
  };

  // Confirm selection: close modal and pass array to parent
  const handleConfirmSelection = () => {
    if (selectedFoldersInModal.length > 0) {
      onFolderSelect(selectedFoldersInModal);
      onClose();
    } else {
      alert("Please select at least one folder.");
    }
  };

  const showTreeAgain = () => {
    setView('tree');
  };

  const clearAllFoldersInModal = () => {
    setSelectedFoldersInModal([]);
    setView('tree');
  };

  const handleRefresh = async () => {
    setLoading(true);
    if (useSystemDrive) {
      // Refresh shared drives for system Google Drive
      await fetchSharedDrives();
      // If a drive is selected, refresh its folders
      if (rootFolderId) {
        await fetchFolders(rootFolderId);
      }
    } else if (storageConnection) {
      // Refresh folders for user's storage
      await fetchFolders(rootFolderId || 'root');
    }
    setLoading(false);
  };

  return (
          <div className="TreeModalOverlay">
        <div className="TreeModalContainer">
        <div className="modal-header">
          <h2 className="modal-title">
            {view === 'tree' ? 'Select LAS Folder' : 'Selected Folders'}
          </h2>
        </div>
        
        <div className="modal-content">
          {view === 'selected_list' ? (
            /* --- Selected folders list view --- */
            <>
              <div className="rsa-selected-folders-list">
                {selectedFoldersInModal.map((folder, index) => {
                  const path = folder.fullPath || folder.name;
                  const displayPath = path.length > 60 ? path.substring(0, 30) + '...' + path.substring(path.length - 25) : path;
                  return (
                    <div key={`${folder.id}-${index}`} className="rsa-selected-folder-item">
                      <span className="rsa-selected-folder-path" title={path}>{displayPath}</span>
                      <button
                        type="button"
                        className="rsa-selected-folder-remove"
                        onClick={() => removeFolderAt(index)}
                        aria-label="Remove folder"
                        title="Remove"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="rsa-selected-folders-actions">
                <button
                  type="button"
                  className="modal-btn modal-btn-secondary rsa-btn-select-another"
                  onClick={showTreeAgain}
                >
                  <svg className="rsa-btn-select-another-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Select another folder
                </button>
                <button
                  type="button"
                  className="modal-btn modal-btn-secondary rsa-btn-clear-folders"
                  onClick={clearAllFoldersInModal}
                >
                  Clear
                </button>
              </div>
            </>
          ) : (
            /* --- Tree view (unchanged layout) --- */
            <>
          {/* Show storage provider info if using user storage */}
          {!useSystemDrive && storageConnection && (
            <div className="modal-section">
              <div className="modal-section-header">
                <label className="modal-label">Storage Provider:</label>
                <button
                  type="button"
                  onClick={handleRefresh}
                  className="rsa-modal-refresh-btn"
                  title="Refresh Storage"
                >
                  <RefreshIcon className="rsa-modal-refresh-icon" />
                </button>
              </div>
              <div style={{ padding: '8px 12px', background: 'rgba(59, 131, 246, 0.1)', borderRadius: '6px', marginBottom: '15px' }}>
                <span style={{ color: '#60a5fa', fontSize: '14px', fontWeight: '500' }}>
                  {storageConnection.connection_name} ({storageConnection.provider_type})
                </span>
              </div>
            </div>
          )}

          {/* Shared Drive Selector - Only show for system Google Drive */}
          {useSystemDrive && (
            <div className="modal-section">
              <div className="modal-section-header">
                <label className="modal-label">Select a Shared Drive:</label>
                <button
                  type="button"
                  onClick={handleRefresh}
                  className="rsa-modal-refresh-btn"
                  title="Refresh Google Drive"
                >
                  <RefreshIcon className="rsa-modal-refresh-icon" />
                </button>
              </div>
            <select
              className="modal-select"
              value={selectedDrive}
              onChange={(e) => setSelectedDrive(e.target.value)}
            >
              <option value="" className="modal-option">-- Select Shared Drive --</option>
              {Array.isArray(sharedDrives) && sharedDrives.length > 0 ? (
                sharedDrives.map((drive) => (
                  <option key={drive.id} value={drive.id} className="modal-option">
                    {drive.name}
                  </option>
                ))
              ) : (
                <option value="" className="modal-option" disabled>No shared drives available</option>
              )}
            </select>
          </div>
          )}

          {loading ? (
            <div className="modal-loading">Loading folders...</div>
          ) : (
            Array.isArray(folders) && folders.length > 0 ? (
              <ul className="tree">
                {folders.map(folder => (
                  <FolderTreeNode 
                    key={folder.id} 
                    folder={folder} 
                    onSelect={handleSelect} 
                    selectedFolderId={selectedFolder?.id}
                    parentPath=""
                    storageConnection={storageConnection}
                    useSystemDrive={useSystemDrive}
                  />
                ))}
              </ul>
            ) : useSystemDrive && selectedDrive ? (
              <div className="modal-loading modal-loading-message">
                No folders found in this shared drive. Please select a different drive.
              </div>
            ) : useSystemDrive && !selectedDrive ? (
              <div className="modal-loading modal-loading-message">
                Please select a shared drive to view folders.
              </div>
            ) : !useSystemDrive && storageConnection ? (
              <div className="modal-loading modal-loading-message">
                No folders found. Please select a folder from the tree above.
              </div>
            ) : (
              <div className="modal-loading modal-loading-message">
                Loading folders...
              </div>
            )
          )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button className="modal-btn modal-btn-secondary" onClick={onClose}>Cancel</button>
          {view === 'tree' ? (
            <button className="modal-btn modal-btn-primary" onClick={handleAddSelected}>Select</button>
          ) : (
            <button className="modal-btn modal-btn-primary" onClick={handleConfirmSelection} disabled={selectedFoldersInModal.length === 0}>Select</button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Dashboard Projects Panel Component ---
function DashboardProjectsPanel({ projects, runs, selectedRun, onSelectRun, onDeleteProject }) {
  const [hasScroll, setHasScroll] = useState(false);
  const projectsListRef = useRef(null);

  useEffect(() => {
    const checkScroll = () => {
      if (projectsListRef.current) {
        const hasOverflow = projectsListRef.current.scrollHeight > projectsListRef.current.clientHeight;
        setHasScroll(hasOverflow);
      }
    };

    checkScroll();
    // Check on resize and when projects change
    window.addEventListener('resize', checkScroll);
    const timeoutId = setTimeout(checkScroll, 100); // Small delay to ensure DOM is updated

    return () => {
      window.removeEventListener('resize', checkScroll);
      clearTimeout(timeoutId);
    };
  }, [projects]);

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'running': return 'status-running';
      case 'failed': return 'status-failed';
      case 'pending': return 'status-pending';
      default: return 'status-pending';
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="tabs w35 dashboard-projects-panel">
      <div className="summary-header">
        <h3>Projects</h3>
        <span className="project-count">
          {projects.length} projects
        </span>
      </div>
      <div className="summary-content dashboard-projects-content">
        <div className={`projects-list ${hasScroll ? 'has-scroll' : ''}`} ref={projectsListRef}>
          {projects.map((project) => {
            const projectRuns = runs.filter(run => run.project_id === project.id);
            const isSelected = selectedRun && selectedRun.project_id === project.id;
            
            return (
              <div
                key={project.id}
                className={`project-item dashboard-tile-bg ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  const firstRun = projectRuns[0];
                  if (firstRun && onSelectRun) {
                    onSelectRun(firstRun);
                  }
                }}
              >
                <div className="dashboard-project-item-header-row">
                  <div className="analysis-dashboard-project-header">
                    <h4 title={project.name}>{project.name}</h4>
                  </div>
                  {onDeleteProject && (
                    <button
                      type="button"
                      className="dashboard-project-item-delete"
                      title="Remove project"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDeleteProject(project.id);
                      }}
                      aria-label="Remove project"
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  )}
                </div>
                <p className="project-description">{project.description}</p>
                <div className="project-stats">
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">Total</span>
                    <span className="analysis-dashboard-stat-value">{project.total_analyses}</span>
                  </div>
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">Completed</span>
                    <span className="analysis-dashboard-stat-value completed">{project.completed_analyses}</span>
                  </div>
                  <div className="analysis-dashboard-stat-item">
                    <span className="analysis-dashboard-stat-label">Running</span>
                    <span className="analysis-dashboard-stat-value running">{project.running_analyses}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// --- Summary Component ---
function RSASummary({ 
  existingProjectName, 
  selectedProjectId,
  services, 
  selectedFolders, 
  selectedDriveName,
  roadDetectionCategory, 
  horizontalData,
  EmbankmentData,
  selectedStorageProvider,
  onSubmit,
  onClear
}) {
  const serviceMapping = {
    'tiling': 'Tiling',
    'csf': 'CSF',
    'road_detection': 'Road Detection',
    'car_removal': 'Car Removal',
    'file_transfer': 'File Transfer',
    'create_rsa': 'Analysis',
    'psd_classification': 'PSD Classification',
    'horizontal': 'Horizontal Alignment',
    'embankment': 'Embankment Analysis',
    'automatic_reports':'Automatic Reports',
    // New service value to display name mappings (with abbreviations)
    'stopping_sight_distance': 'Stopping Sight Distance (SSD)',
    'passing_sight_distance': 'Passing Sight Distance (PSD)',
    'functional_safety_analysis': 'Functional Safety Analysis (FSA)',
    'intersection_sight_distance': 'Intersection Sight Distance (ISD)',
    'sunglare_analysis': 'Sunglare Analysis',
    'guardrails_warrant': 'Guardrails Warrant',
    'existing_guardrails': 'Existing Guardrails',
    'clear_zone_analysis': 'Clear Zone Analysis'
  };

  const analysisMapping = {
    'SSD': 'SSD',
    'PSD': 'PSD',
    'FSA': 'FSA',
    'ISD': 'ISD',
    'GLR': 'GLR',
  };

  const storageProviderMapping = {
    'system_google_drive': { name: 'System Drive', icon: GoogleDriveIcon, iconSize: 16 },
    'google_drive': { name: 'Google Drive', icon: GoogleDriveIcon, iconSize: 16 },
    's3': { name: 'AWS S3', icon: AmazonS3Icon, iconSize: 16 },
    'azure': { name: 'Microsoft Azure', icon: AzureIcon, iconSize: 16 },
    'gcp': { name: 'Google Cloud Storage', icon: GoogleCloudIcon, iconSize: 16 }
  };

  const renderStorageProviderButton = () => {
    if (!selectedStorageProvider) return null;
    
    const provider = storageProviderMapping[selectedStorageProvider];
    if (!provider) return null;

    const IconComponent = provider.icon;
    
    return (
      <button 
        className="toggle-btn-rsa task-button storage-provider-btn storage-provider-btn-summary selected"
        type="button"
        disabled
        style={{ cursor: 'default' }}
      >
        <IconComponent className="storage-provider-icon" style={{ width: `${provider.iconSize}px`, height: `${provider.iconSize}px` }} />
        <span>{provider.name}</span>
      </button>
    );
  };

  return (
    <div className="tabs w35">
      <div className="summary-header">
        <h3>Analysis Selection Summary</h3>
      </div>
      <div className="summary-content">
        <div className="summary-section">
          <h4>Project Details</h4>
          <div className="summary-item">
            <span className="summary-label">Name:</span>
            <span className="summary-value">
              {existingProjectName}
            </span>
          </div>
          {selectedProjectId && (
            <div className="summary-item">
              <span className="summary-label">Project ID:</span>
              <span className="summary-value">{selectedProjectId}</span>
            </div>
          )}
        </div>

        <div className="summary-section">
          <h4>Selected Services</h4>
          {services.length > 0 ? (
            <div className="summary-list">
              {services.map(service => (
                <div key={service} className="summary-tag">
                  {serviceMapping[service] || service}
                </div>
              ))}
            </div>
          ) : (
            <span className="summary-empty">No services selected</span>
          )}
        </div>

        <div className="summary-section">
          <h4>Cloud Storage Service</h4>
          {selectedStorageProvider ? (
            <div className="summary-storage-provider">
              {renderStorageProviderButton()}
            </div>
          ) : (
            <span className="summary-empty">No storage provider selected</span>
          )}
        </div>

        <div className="summary-section">
          <h4>Folder Selection</h4>
          {selectedDriveName && (
            <div className="summary-item">
              <span className="summary-label">Shared Drive:</span>
              <span className="summary-value">{selectedDriveName}</span>
            </div>
          )}
          {selectedFolders && selectedFolders.length > 0 ? (
            <div className="summary-folders-list">
              <span className="summary-label">Folder{selectedFolders.length > 1 ? 's' : ''}:</span>
              {selectedFolders.map((folder, index) => {
                const path = folder.fullPath || folder.name;
                const displayPath = path.length > 50 ? path.substring(0, 25) + '...' + path.substring(path.length - 20) : path;
                return (
                  <div key={folder.id + (folder.name || '') + index} className="summary-item summary-folder-item">
                    <span className="summary-value" title={path}>{displayPath}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="summary-empty">No folder selected</span>
          )}
        </div>
       </div>
       
               {/* Run Analysis and Clear Buttons at the bottom */}
        <div className="summary-button-container">
          <div className="summary-buttons-row">
            <button className='toggle-btn-rsa-run' type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSubmit(e); }}>
              Run Analysis
            </button>
            <button className='toggle-btn-rsa-clear' type="button" onClick={onClear}>
              Clear
            </button>
          </div>
        </div>
     </div>
   );
 }

function PayloadForm() {
  // Cached project list and dropdown options
  const [allProjects, setAllProjects] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  
  // State for selected project from dropdown
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  // Store an array of selected folder objects (order of selection)
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [selectedDriveName, setSelectedDriveName] = useState('');
  const [roadDetectionCategory, setRoadDetectionCategory] = useState('');
  const [lidarType, setLidarType] = useState('');
  const [horizontalData, setHorizontalData] = useState(null);
  const [EmbankmentData, setEmbankmentData] = useState(null); // State for Embankment data
  
  // Storage provider state
  const [selectedStorageProvider, setSelectedStorageProvider] = useState('system_google_drive');
  const [storageConnections, setStorageConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [useSystemGoogleDrive, setUseSystemGoogleDrive] = useState(true);
  
  // Only existing projects are supported
  const [existingProjectName, setExistingProjectName] = useState('');

  // State for the analysis types and services (toggle buttons)
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [services, setServices] = useState([]);
  // State to track selected task buttons and their analysis_template_ids
  const [selectedTasks, setSelectedTasks] = useState([]);
  console.log(services);
  // Instead of a global loading state, we now track each payload request.
  const [requests, setRequests] = useState([]);
  // Additional state for our new custom radio button group (e.g. Time selection)
  const [timeOption, setTimeOption] = useState('');

  // --- State for Folder Tree Popup ---
  const [showFolderTreePopup, setShowFolderTreePopup] = useState(false);
  const [groupSize, setGroupSize] = useState(25);
  
  // --- State for custom dropdown ---
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const dropdownRef = useRef(null);
  
  // --- State for clear confirmation popup ---
  const [showClearPopup, setShowClearPopup] = useState(false);

  // --- Slide-down notification (replaces alert) ---
  const [notification, setNotification] = useState({ message: null, leaving: false });
  const notificationTimeoutRef = useRef(null);
  const showNotification = (message) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification({ message, leaving: false });
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(prev => ({ ...prev, leaving: true }));
      notificationTimeoutRef.current = setTimeout(() => {
        setNotification({ message: null, leaving: false });
        notificationTimeoutRef.current = null;
      }, 300);
    }, 3000);
  };
  useEffect(() => () => {
    if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
  }, []);

  // --- State for storage connection modal ---
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connectionProvider, setConnectionProvider] = useState(null);
  
  // --- State for main content tabs ---
  const [activeMainTab, setActiveMainTab] = useState('analysis');
  
  // --- State for Dashboard ---
  const [dashboardSelectedRun, setDashboardSelectedRun] = useState(null);
  const [optimisticRuns, setOptimisticRuns] = useState([]);

  // Connect Dashboard to backend/Redis: load user runs and build projects from runs
  const dashboardUserEmail = sessionStorage.getItem('userEmail') || null;
  const { runs: apiRuns, loading: dashboardLoading, error: dashboardError, refresh: refreshDashboardRuns } = useUserRuns(dashboardUserEmail, {
    autoRefresh: true,
    refreshInterval: 30000,
  });

  // Merge API runs with optimistic runs (optimistic = just submitted; dedupe by run_id, API wins)
  const displayRuns = useMemo(() => {
    const apiRunIds = new Set((apiRuns || []).map((r) => r.run_id));
    const onlyOptimistic = (optimisticRuns || []).filter((o) => !apiRunIds.has(o.run_id));
    const merged = [...(apiRuns || []), ...onlyOptimistic];
    merged.sort((a, b) => {
      const tA = a.last_updated?.getTime() ?? a.created_at?.getTime() ?? 0;
      const tB = b.last_updated?.getTime() ?? b.created_at?.getTime() ?? 0;
      return tB - tA;
    });
    return merged;
  }, [apiRuns, optimisticRuns]);

  // Backward compatibility: alias for any leftover references (e.g. hot-reload)
  const dashboardMockRuns = displayRuns;

  // Build project tiles from runs (group by project_id)
  const dashboardProjectsData = useMemo(() => {
    const byProject = new Map();
    for (const run of displayRuns) {
      const id = run.project_id ?? 'global';
      if (!byProject.has(id)) {
        byProject.set(id, {
          id,
          name: run.project_name ?? run.name ?? `Project ${id}`,
          description: `Pipeline runs for project ${id}`,
          total_analyses: 0,
          completed_analyses: 0,
          running_analyses: 0,
          last_updated: run.last_updated ?? run.created_at ?? new Date(),
        });
      }
      const p = byProject.get(id);
      p.total_analyses += 1;
      if (run.status === 'completed') p.completed_analyses += 1;
      if (run.status === 'processing' || run.status === 'running') p.running_analyses += 1;
      const runTime = run.last_updated?.getTime() ?? run.created_at?.getTime();
      if (runTime && runTime > (p.last_updated?.getTime() ?? 0)) p.last_updated = run.last_updated ?? run.created_at;
    }
    return Array.from(byProject.values()).sort((a, b) => (b.last_updated?.getTime() ?? 0) - (a.last_updated?.getTime() ?? 0));
  }, [displayRuns]);

  // Prune optimistic runs older than 5 min (API should have picked them up)
  useEffect(() => {
    if (optimisticRuns.length === 0) return;
    const id = setInterval(() => setOptimisticRuns((prev) => prev.filter((o) => {
      const age = Date.now() - (o.created_at?.getTime() ?? o.last_updated?.getTime() ?? 0);
      return age < 5 * 60 * 1000;
    })), 60 * 1000);
    return () => clearInterval(id);
  }, [optimisticRuns.length]);

  // Legacy mock data (kept for reference; Dashboard now uses displayRuns + dashboardProjectsData)
  const dashboardMockAnalysesData = [
    {
      id: 'analysis_123',
      project_name: 'Highway LiDAR Analysis',
      project_id: 'project_789',
      status: 'running',
      start_time: new Date(Date.now() - 2 * 60 * 60 * 1000),
      progress: { percentage: 65, completed_steps: 3, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'completed', progress: 100, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'completed', progress: 100, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'running', progress: 75, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'pending', progress: 0, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis', status: 'pending', progress: 0, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'pending', progress: 0, description: 'Generating final analysis report' }
      ]
    },
    {
      id: 'analysis_456',
      project_name: 'Urban Road Analysis',
      project_id: 'project_789',
      status: 'completed',
      start_time: new Date(Date.now() - 4 * 60 * 60 * 1000),
      progress: { percentage: 100, completed_steps: 6, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'completed', progress: 100, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'completed', progress: 100, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'completed', progress: 100, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'completed', progress: 100, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis', status: 'completed', progress: 100, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'completed', progress: 100, description: 'Generating final analysis report' }
      ]
    },
    {
      id: 'analysis_789',
      project_name: 'Bridge Analysis Project',
      project_id: 'project_452',
      status: 'running',
      start_time: new Date(Date.now() - 30 * 60 * 1000),
      progress: { percentage: 25, completed_steps: 1, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'completed', progress: 100, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'running', progress: 50, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'pending', progress: 0, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'pending', progress: 0, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis', status: 'pending', progress: 0, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'pending', progress: 0, description: 'Generating final analysis report' }
      ]
    },
    {
      id: 'analysis_486',
      project_name: 'West Kingman Interchange Analysis',
      project_id: 'project_486',
      status: 'pending',
      start_time: new Date(Date.now() - 15 * 60 * 1000),
      progress: { percentage: 0, completed_steps: 0, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'pending', progress: 0, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'pending', progress: 0, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'pending', progress: 0, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'pending', progress: 0, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis', status: 'pending', progress: 0, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'pending', progress: 0, description: 'Generating final analysis report' }
      ]
    },
    {
      id: 'analysis_416',
      project_name: 'SR 516 Lake-Orange County Analysis',
      project_id: 'project_416',
      status: 'completed',
      start_time: new Date(Date.now() - 6 * 60 * 60 * 1000),
      progress: { percentage: 100, completed_steps: 6, total_steps: 6 },
      steps: [
        { name: 'tiling', display_name: 'File Tiling', status: 'completed', progress: 100, description: 'Breaking large LiDAR files into manageable tiles for processing' },
        { name: 'csf', display_name: 'Ground Classification', status: 'completed', progress: 100, description: 'Classifying ground points using CSF algorithm' },
        { name: 'car_removal', display_name: 'Vehicle Removal', status: 'completed', progress: 100, description: 'Removing vehicles and other moving objects from the point cloud' },
        { name: 'road_detection', display_name: 'Road Detection', status: 'completed', progress: 100, description: 'Detecting road boundaries' },
        { name: 'embankment', display_name: 'Embankment Analysis', status: 'completed', progress: 100, description: 'Analyzing road embankments' },
        { name: 'report_generation', display_name: 'Report Generation', status: 'completed', progress: 100, description: 'Generating final analysis report' }
      ]
    }
  ];
  
  const dashboardMockProjectsData = [
    { id: 'project_789', name: 'CFX SR-538 Poinciana Pkwy', description: 'Comprehensive analysis of highway sections for maintenance planning', total_analyses: 2, completed_analyses: 1, running_analyses: 1, last_updated: new Date(Date.now() - 30 * 60 * 1000) },
    { id: 'project_452', name: 'ADOT Climbing Passing RSA', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 1, completed_analyses: 0, running_analyses: 1, last_updated: new Date(Date.now() - 30 * 60 * 1000) },
    { id: 'project_486', name: 'ADOT West Kingman Interchange', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 1, completed_analyses: 0, running_analyses: 0, last_updated: new Date(Date.now() - 30 * 60 * 1000) },
    { id: 'project_416', name: 'CFX SR 516 Lake-Orange County', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 1, completed_analyses: 1, running_analyses: 0, last_updated: new Date(Date.now() - 30 * 60 * 1000) },
    { id: 'project_521', name: 'CFX SR-538 Poinciana Pkwy', description: 'Comprehensive analysis of highway sections for maintenance planning', total_analyses: 3, completed_analyses: 2, running_analyses: 1, last_updated: new Date(Date.now() - 45 * 60 * 1000) },
    { id: 'project_623', name: 'ADOT Climbing Passing RSA', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 2, completed_analyses: 1, running_analyses: 1, last_updated: new Date(Date.now() - 20 * 60 * 1000) },
    { id: 'project_734', name: 'ADOT West Kingman Interchange', description: 'Structural analysis of bridge components using LiDAR data', total_analyses: 1, completed_analyses: 0, running_analyses: 1, last_updated: new Date(Date.now() - 15 * 60 * 1000) }
  ];
  
  // Auto-select first run if none selected (Dashboard uses displayRuns)
  useEffect(() => {
    if (activeMainTab === 'dashboard' && displayRuns.length > 0 && !dashboardSelectedRun) {
      setDashboardSelectedRun(displayRuns[0]);
    }
  }, [activeMainTab, displayRuns.length]);

  const handleDeleteProject = async (projectId) => {
    const userEmail = sessionStorage.getItem('userEmail');
    if (!userEmail) {
      showNotification('Please log in to remove projects.');
      return;
    }
    try {
      await progressService.deleteProjectRuns(userEmail, projectId);
      setOptimisticRuns((prev) => prev.filter((r) => r.project_id !== projectId));
      refreshDashboardRuns();
      if (dashboardSelectedRun?.project_id === projectId) {
        setDashboardSelectedRun(null);
      }
      showNotification('Project removed from list.');
    } catch (err) {
      console.error('Delete project runs failed:', err);
      showNotification(err.response?.data?.error || 'Failed to remove project.');
    }
  };

  // Function to fetch projects from the API and reset dropdown state.
  const fetchProjects = async () => {
    console.log("🔧 RSAAnalysis - fetchProjects called");
    setIsLoadingProjects(true);
    try {
      // Use API_BASE_URL for local development, relative URL for production (CloudFront)
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/projectsSearch?query=`
        : '/api/projectsSearch?query=';
      console.log("🔧 RSAAnalysis - Making request to:", url);
      console.log("🔧 RSAAnalysis - ENVIRONMENT:", ENVIRONMENT);
      console.log("🔧 RSAAnalysis - API_BASE_URL:", API_BASE_URL);
      const response = await axios.get(url);
      setAllProjects(response.data);
      sessionStorage.setItem('allProjects', JSON.stringify(response.data));
      // Clear dropdown selections and search results
      setSearchResults([]);
      setSelectedProjectId(null);
      setExistingProjectName('');
      console.log("✅ RSAAnalysis - Projects refreshed from API");
    } catch (error) {
      console.error("❌ RSAAnalysis - Error refreshing projects:", error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Function to fetch shared drives from the API
  const fetchSharedDrives = async () => {
    try {
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/google-drive/shared-drives`
        : '/api/google-drive/shared-drives';
      console.log("🔄 RSAAnalysis - Preloading shared drives from:", url);
      const response = await axios.get(url);
      // Handle both response formats and ensure we always have an array
      const drives = response.data?.drives || response.data?.shared_drives || [];
      const drivesArray = Array.isArray(drives) ? drives : [];
      // Cache in sessionStorage for FolderTreePopup to use
      sessionStorage.setItem('sharedDrives', JSON.stringify(drivesArray));
      console.log("✅ RSAAnalysis - Shared drives preloaded and cached:", drivesArray.length);
    } catch (err) {
      console.error("❌ RSAAnalysis - Error preloading shared drives:", err);
      // Don't show error to user, just log it
    }
  };

  // Helper function to get token (shared with StorageConnectionModal)
  const getAuthToken = async () => {
    try {
      // First, try to get token from AWS Amplify Auth (primary method)
      try {
        const session = await Auth.currentSession();
        if (session && session.getIdToken()) {
          const idToken = session.getIdToken().getJwtToken();
          console.log('✅ [getAuthToken] Got token from Amplify Auth');
          return idToken;
        }
      } catch (amplifyError) {
        console.log('ℹ️ [getAuthToken] Amplify Auth not available, trying other methods:', amplifyError.message);
      }
      
      // Fallback: Check multiple possible keys in storage (in order of likelihood)
      let token = sessionStorage.getItem('bimLiveAuthToken'); // Primary key used by this app
      
      if (!token) {
        token = sessionStorage.getItem('token');
      }
      if (!token) {
        token = sessionStorage.getItem('cognito_token');
      }
      if (!token) {
        token = sessionStorage.getItem('idToken');
      }
      if (!token) {
        // Check localStorage as fallback
        token = localStorage.getItem('bimLiveAuthToken') || 
                localStorage.getItem('token') || 
                localStorage.getItem('cognito_token') || 
                localStorage.getItem('idToken');
      }
      
      // Check cookies as last resort
      if (!token) {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'cognito_token' || name === 'token') {
            token = value;
            break;
          }
        }
      }
      
      console.log('🔍 [getAuthToken] Raw token from storage:', token ? `${token.substring(0, 20)}...` : 'null');
      
      if (!token) {
        console.warn('⚠️ [getAuthToken] No token found in sessionStorage, localStorage, cookies, or Amplify');
        console.log('🔍 [getAuthToken] Available sessionStorage keys:', Object.keys(sessionStorage));
        return null;
      }
      
      // Try to parse as JSON (token might be stored as JSON string)
      try {
        const parsed = JSON.parse(token);
        console.log('✅ [getAuthToken] Token is JSON, parsed structure:', typeof parsed, parsed ? Object.keys(parsed) : 'null');
        
        if (parsed && typeof parsed === 'string') {
          // Token is a JSON stringified string
          console.log('✅ [getAuthToken] Using parsed string token');
          return parsed;
        } else if (parsed && parsed.token) {
          // Token is an object with a token property
          console.log('✅ [getAuthToken] Using parsed.token');
          return parsed.token;
        } else if (parsed && parsed.idToken) {
          // Token is an object with an idToken property (Cognito)
          console.log('✅ [getAuthToken] Using parsed.idToken');
          return parsed.idToken;
        } else if (parsed && parsed.accessToken) {
          // Token is an object with an accessToken property
          console.log('✅ [getAuthToken] Using parsed.accessToken');
          return parsed.accessToken;
        } else {
          // If parsed is an object but we don't know the structure, log it
          console.warn('⚠️ [getAuthToken] Parsed token is object but no known property found:', parsed);
          // Try to return the whole object as string (some APIs accept this)
          return JSON.stringify(parsed);
        }
      } catch (parseError) {
        // Not JSON, use as-is (raw JWT token)
        console.log('✅ [getAuthToken] Token is not JSON, using as-is (raw JWT)');
        return token;
      }
    } catch (e) {
      console.error('❌ [getAuthToken] Error getting auth token:', e);
      return null;
    }
  };

  // Load user's storage connections
  const loadStorageConnections = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        // User not logged in - use system Google Drive
        setUseSystemGoogleDrive(true);
        setSelectedStorageProvider('system_google_drive');
        return;
      }
      
      const url = ENVIRONMENT === 'local' 
        ? `${API_BASE_URL}/api/storage/connections`
        : '/api/storage/connections';
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const connections = response.data.connections || [];
      setStorageConnections(connections);
      
      // Check for default connection
      const defaultConn = connections.find(c => c.is_default && c.is_active);
      if (defaultConn) {
        setSelectedConnection(defaultConn);
        setSelectedStorageProvider(defaultConn.provider_type);
        setUseSystemGoogleDrive(false);
      } else {
        // No default connection - use system Google Drive
        setSelectedStorageProvider('system_google_drive');
        setUseSystemGoogleDrive(true);
        setSelectedConnection(null);
      }
    } catch (error) {
      console.error('Error loading storage connections:', error);
      // Fallback to system Google Drive
      setUseSystemGoogleDrive(true);
      setSelectedStorageProvider('system_google_drive');
    }
  };

  // Check which providers user has connected
  const hasGoogleDriveConnection = storageConnections.some(c => c.provider_type === 'google_drive' && c.is_active);
  const hasS3Connection = storageConnections.some(c => c.provider_type === 's3' && c.is_active);
  const hasAzureConnection = storageConnections.some(c => c.provider_type === 'azure' && c.is_active);
  const hasGCPConnection = storageConnections.some(c => c.provider_type === 'gcp' && c.is_active);

  // Check for OAuth callback success
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const storageConnected = urlParams.get('storage_connected');
    const success = urlParams.get('success');
    
    if (storageConnected && success === 'true') {
      // Reload storage connections
      loadStorageConnections();
      
      // Show success message with proper provider name
      const providerNames = {
        'google_drive': 'Google Drive',
        'azure': 'Microsoft Azure',
        'gcp': 'Google Cloud Storage',
        's3': 'AWS S3'
      };
      const providerName = providerNames[storageConnected] || storageConnected;
      alert(`${providerName} connected successfully!`);
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // On mount, preload projects and shared drives from API in the background
  // This ensures the dropdowns are ready when the user opens them
  useEffect(() => {
    // First, load from sessionStorage if available (for immediate display)
    const storedProjects = sessionStorage.getItem('allProjects');
    if (storedProjects) {
      const parsedProjects = JSON.parse(storedProjects);
      setAllProjects(parsedProjects);
      setSearchResults([]); // Keep dropdown empty initially
      console.log("✅ Projects loaded from sessionStorage (cached)");
    }
    
    // Load user's storage connections
    loadStorageConnections();
    
    // Always fetch fresh data in the background on first mount
    // This ensures the dropdowns are ready when user opens them
    console.log("🔄 Preloading projects and shared drives from API on RSA-Analysis mount...");
    fetchProjects().catch(error => {
      console.error("❌ Failed to preload projects:", error);
      // Don't show error to user, just log it - cached data will be used if available
    });
    
    // Preload shared drives in the background
    fetchSharedDrives().catch(error => {
      console.error("❌ Failed to preload shared drives:", error);
      // Don't show error to user, just log it
    });
  }, []);

  // Persist requests in localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('requests', JSON.stringify(requests));
  }, [requests]);

  // Load persisted requests from localStorage on mount
  useEffect(() => {
    const storedRequests = localStorage.getItem('requests');
    if (storedRequests) {
      setRequests(JSON.parse(storedRequests));
    }
  }, []);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update CSS custom properties for dynamic layout when services change
  useEffect(() => {
    const rightPanels = document.querySelector('.right-panels');
    if (rightPanels) {
      const hasEmbankmentPanel = services.includes('horizontal') || services.includes('embankment');
      rightPanels.style.setProperty('--embankment-panel-width', hasEmbankmentPanel ? '380px' : '0px');
    }
  }, [services]);

  // Filter the cached projects locally as the user type.
  const handleInputChange = (inputValue) => {
    console.log('handleInputChange called with:', inputValue);
    console.log('allProjects length:', allProjects.length);
    console.log('allProjects sample:', allProjects.slice(0, 3));
    
    if (!inputValue) {
      setSearchResults([]);
      setDropdownOpen(false);
      return inputValue;
    }
    
    // Always open dropdown when typing
    setDropdownOpen(true);
    
    const filtered = allProjects.filter(proj =>
      proj.name.toLowerCase().includes(inputValue.toLowerCase())
    );
    
    console.log('Filtered results:', filtered);
    console.log('Setting searchResults to:', filtered);
    setSearchResults(filtered);
    
    return inputValue;
  };


  // Mapping from task button display names to analysis_template_ids
  const analysisTemplateIdMapping = {
    'Stopping Sight Distance (SSD)': '1rlzhq4UcaG89Z6-BgsvkzEoy07c-_FAh', // SSD
    'Passing Sight Distance (PSD)': '1WSPVWSMQm9ZNVZYi63glWnHtovalxiSw', // PSD
    'Functional Safety Analysis (FSA)': '1o5Tkz_1kZ3L9duI1m8iCUru3eg6kkj7o', // FSA
    'Intersection Sight Distance (ISD)': '14Wcpu_nadstIKGkMfydgdCn91JuTfN7a', // ISD
    'Sunglare Analysis': '', // Not provided in mapping
    'Guardrails Warrant': '1YrgG9gBrxU2qPP0OdeAF875RDvwyR5Ax', // Guardrail_Warrant
    'Horizontal Alignment': '1ui2FC_9umPQwJnVRxvGLFG3vheE2jzu8', // Horizontal
    'Existing Guardrails': '1_UYE80G3n99vcEHPzOVFappGDXnEyThg', // Existing_Guardrails
    'Embankment Analysis': '1wgbWg82DReXKI5SYbLVI4Ib6OGFbY7DY', // Embankment
    'Clear Zone Analysis': '1R7RxChS8YYc1v4zu_1moBj5pnVA-NKmQ' // ClearZone
  };

  // Modified handleSubmit: use new payload structure with analysis_template_ids
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!existingProjectName) {
      alert("Please select existing project.");
      return;
    }

    if (selectedTasks.length === 0) {
      alert("Please select at least one task.");
      return;
    }      
    if (!selectedFolders || selectedFolders.length === 0) {
      alert("Please select at least one folder from the folder tree.");
      return;
    }

    // Build folder_ids array from selected folders (order of selection; backend expects list of strings)
    const folderIds = selectedFolders
      .map(f => f.id ?? f.folder_id ?? f.folderId)
      .filter(id => id != null && id !== '')
      .map(String);

    if (folderIds.length === 0) {
      alert("Selected folders have no valid folder IDs.");
      return;
    }
    
    // Generate a unique id for this request
    const requestId = Date.now();
    
    // Create a new request record with a pending status
    const newRequestEntry = {
      id: requestId,
      projectType: 'existing',
      folderIds,
      projectName: existingProjectName,
      status: 'pending',
      response: '',
    };
    setRequests(prev => [...prev, newRequestEntry]);
  
    try {
      // Build analysis_template_ids array from selected tasks
      const analysisTemplateIds = selectedTasks
        .map(taskName => analysisTemplateIdMapping[taskName])
        .filter(id => id && id !== ''); // Filter out empty or undefined IDs
      
      if (analysisTemplateIds.length === 0) {
        alert("No valid analysis templates found for selected tasks.");
        return;
      }
      
      // New payload structure
      const user_email = sessionStorage.getItem('userEmail') || '';
      const processPayload = {
        project_id: selectedProjectId,
        project_name: existingProjectName || undefined,
        folder_ids: folderIds,
        analysis_template_ids: analysisTemplateIds, // Array of analysis template IDs from selected tasks
        horizontal_data: horizontalData || {},
        embankment_data: EmbankmentData || {},
        user_email
      };

      // New server endpoint for RSA analysis
      // Use relative URL for production (goes through CDN /rsa/* behavior), full URL for local/server
      // Check if we're on production domain (123bimlive.com) to ensure correct routing
      const isProductionDomain = window.location.hostname.includes('123bimlive.com');
      const processUrl = (ENVIRONMENT === 'production' || isProductionDomain)
        ? '/rsa/process'
        : `${processApi}/process`;
      
      console.log('🚀 Making API call to process endpoint:', processUrl);
      console.log('🔧 ENVIRONMENT:', ENVIRONMENT);
      console.log('🔧 isProductionDomain:', isProductionDomain);
      console.log('🔧 window.location.hostname:', window.location.hostname);
      console.log('📦 Payload:', processPayload);
      
      const processResponse = await axios.post(
        processUrl,
        processPayload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log('✅ Process API call successful:', processResponse.data);

      // Update the request record for the existing project.
      setRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? { ...req, status: 'completed', response: `Existing project process completed: ${processResponse.data.message}` }
            : req
        )
      );

      // Optimistic UI: add new run tile to Dashboard (backend returns 200 or 202 with run_id)
      const runId = processResponse.data?.run_id;
      if (runId && (processResponse.status === 200 || processResponse.status === 202)) {
        const newRun = {
          run_id: runId,
          project_id: selectedProjectId,
          project_name: existingProjectName || undefined,
          name: existingProjectName || `Run ${runId}`,
          description: `Pipeline run for project ${selectedProjectId}`,
          status: 'processing',
          created_at: new Date(),
          last_updated: new Date(),
          user_email: user_email,
          services: selectedTasks || [],
          overall_progress: 0,
          total_analyses: 1,
          completed_analyses: 0,
          running_analyses: 1,
        };
        setOptimisticRuns((prev) => [newRun, ...prev]);
        refreshDashboardRuns();
        setActiveMainTab('dashboard');
        setDashboardSelectedRun(newRun);
      }

      // Show success message - user stays on RSA-Analysis page (or moved to Dashboard)
      showNotification('Analysis submitted successfully!');
    } catch (error) {
      console.error('❌ Process API call failed:', error);
      console.error('❌ Error response:', error.response?.data);
      
      // Update the request record with an error message if the API call fails.
      setRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? { ...req, status: 'error', response: error.response?.data?.message || 'There was an error submitting the payload(s).' }
            : req
        )
      );
      
      // Don't redirect on error - show error to user instead
      showNotification(`Error submitting analysis: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    }
  };
  
  // Legacy service mapping (kept for backward compatibility if needed)
  const serviceMapping = {
    'Tiling': 'tiling',
    'CSF': 'csf',
    'Road Detection': 'road_detection',
    'Car Removal': 'car_removal',
    'File Transfer': 'file_transfer',
    'Analysis': 'create_rsa',
    'PSD Classification': 'psd_classification',
    'Horizontal': 'horizontal',
    'Embankment': 'embankment',
    'Automatic Reports':'automatic_reports',
    // New task name mappings (with abbreviations)
    'Stopping Sight Distance (SSD)': 'stopping_sight_distance',
    'Passing Sight Distance (PSD)': 'passing_sight_distance',
    'Functional Safety Analysis (FSA)': 'functional_safety_analysis',
    'Intersection Sight Distance (ISD)': 'intersection_sight_distance',
    'Sunglare Analysis': 'sunglare_analysis',
    'Guardrails Warrant': 'guardrails_warrant',
    'Horizontal Alignment': 'horizontal',
    'Existing Guardrails': 'existing_guardrails',
    'Embankment Analysis': 'embankment',
    'Clear Zone Analysis': 'clear_zone_analysis'
  };

  // Function to handle clear button click
  const handleClearClick = () => {
    setShowClearPopup(true);
  };

  // Function to clear all form data
  const handleClearConfirm = () => {
    // Reset all form states
    setExistingProjectName('');
    setSelectedProjectId(null);
    setServices([]);
    setAnalysisTypes([]);
    setSelectedTasks([]);
    setSelectedFolders([]);
    setSelectedDriveName('');
    setLidarType('');
    setHorizontalData(null);
    setEmbankmentData(null);
    setGroupSize(25);
    setShowClearPopup(false);
  };

  // Function to cancel clear operation
  const handleClearCancel = () => {
    setShowClearPopup(false);
  };



    return (
    <div className="w100RSA">
      {/* Slide-down notification from top */}
      {notification.message && (
        <div
          className={`rsa-slide-notification ${notification.leaving ? 'rsa-slide-notification-leaving' : ''}`}
          role="alert"
        >
          {notification.message}
        </div>
      )}
      <div className={`main-content-area ${activeMainTab === 'dashboard' ? 'main-content-area-full-width' : ''}`}>
        {/* Tab Navigation */}
        <div className="main-content-tabs">
          <button 
            className={`main-content-tab ${activeMainTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('analysis')}
          >
            <i className="fas fa-chart-line"></i>
            <span>Analysis</span>
          </button>
          <button 
            className={`main-content-tab ${activeMainTab === 'setup' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('setup')}
          >
            <i className="fas fa-sliders-h"></i>
            <span>Setup</span>
          </button>
          <button 
            className={`main-content-tab ${activeMainTab === 'paths' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('paths')}
          >
            <i className="fas fa-route"></i>
            <span>Paths</span>
          </button>
          <button 
            className={`main-content-tab ${activeMainTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('dashboard')}
          >
            <i className="fas fa-chart-pie"></i>
            <span>Dashboard</span>
          </button>
          <button 
            className={`main-content-tab main-content-tab-settings ${activeMainTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('settings')}
          >
            <i className="fas fa-cog"></i>
            <span>Settings</span>
          </button>
        </div>
        
        {/* Tab Content Container */}
        <div className="main-content-tabs-container">
          {/* Analysis Tab Panel */}
          <div className={`main-content-tab-panel main-content-tab-panel-analysis ${activeMainTab === 'analysis' ? 'active' : ''}`}>
            <div className="tab-content-RSA">
          {/* Existing Project Selection */}
          <div className='project-input-container'>
            <div className='project-input-section'>
              <label>Type project name to select :</label>
              <div className='project-input-wrapper'>
                <div className="custom-dropdown" ref={dropdownRef}>
                  <div className="input-with-arrow">
                    <input
                      type="text"
                      className="project-input-field"
                      placeholder="Type project name to select"
                      value={existingProjectName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setExistingProjectName(value);
                        handleInputChange(value);
                      }}
                      onFocus={() => setDropdownOpen(true)}
                    />
                    <div 
                      className={`dropdown-arrow ${dropdownOpen ? 'rotated' : ''} dropdown-arrow-clickable`}
                      onClick={() => {
                        if (allProjects.length === 0 && !isLoadingProjects) {
                          fetchProjects();
                        }
                        setDropdownOpen(!dropdownOpen);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  {dropdownOpen && (
                    <div className="dropdown-menu">
                      {isLoadingProjects ? (
                        // Show loading state
                        <div className="dropdown-option no-results">
                          Loading projects...
                        </div>
                      ) : searchResults.length > 0 ? (
                        // Show filtered results when typing
                        searchResults.map((proj) => (
                          <div
                            key={proj.id}
                            className="dropdown-option"
                            onClick={() => {
                              setSelectedProjectId(proj.id);
                              setExistingProjectName(proj.name);
                              setDropdownOpen(false);
                            }}
                          >
                            {proj.name}
                          </div>
                        ))
                      ) : existingProjectName ? (
                        // Show "no results" when typing but no matches
                        <div className="dropdown-option no-results">
                          No projects found matching "{existingProjectName}"
                        </div>
                      ) : allProjects.length > 0 ? (
                        // Show all projects when input is empty and projects exist
                        allProjects.map((proj) => (
                          <div
                            key={proj.id}
                            className="dropdown-option"
                            onClick={() => {
                              setSelectedProjectId(proj.id);
                              setExistingProjectName(proj.name);
                              setDropdownOpen(false);
                            }}
                          >
                            {proj.name}
                          </div>
                        ))
                      ) : (
                        // Show message when no projects are loaded
                        <div className="dropdown-option no-results">
                          No projects available. Click refresh to load projects.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button 
                  type="button" 
                  onClick={() => { fetchProjects(); setExistingProjectName(''); }}
                  className="rsa-refresh-btn"
                >
                  <RefreshIcon className="rsa-refresh-icon" />
                </button>
              </div>
            </div>
          </div>
         {/* Toggle Buttons for Required Services */}
     <div className="tasks-selection-container">
      <p className='pHeader'>Select Required Tasks:</p>
                 <div className="tasks-buttons-container">
        {['Stopping Sight Distance (SSD)', 'Passing Sight Distance (PSD)', 'Functional Safety Analysis (FSA)', 'Intersection Sight Distance (ISD)', 'Sunglare Analysis', 'Guardrails Warrant', 'Horizontal Alignment','Existing Guardrails','Embankment Analysis',"Clear Zone Analysis"].map((type, index) => {
          const templateId = analysisTemplateIdMapping[type];
          const isSelected = selectedTasks.includes(type);
          
          return (
            <button
              key={type}
              type="button"
              onClick={() => {
                if (templateId) {
                  if (isSelected) {
                    setSelectedTasks(prev => prev.filter(t => t !== type));
                    // Also remove from services for backward compatibility
                    const serviceValue = serviceMapping[type];
                    if (serviceValue) {
                      setServices(prev => prev.filter(s => s !== serviceValue));
                    }
                  } else {
                    setSelectedTasks(prev => [...prev, type]);
                    // Also add to services for backward compatibility
                    const serviceValue = serviceMapping[type];
                    if (serviceValue) {
                      setServices(prev => [...prev, serviceValue]);
                    }
                  }
                } else {
                  console.warn(`No analysis template ID found for: ${type}`);
                }
              }}
              className={`toggle-btn-rsa task-button ${isSelected ? 'selected' : ''}`}
              style={{
                marginBottom: '0'
              }}
            >
              {type}
            </button>
          );
        })}
      </div>
    </div>

      {/* Folder Selector Button */}
      <div className='las-folder-selector'>
        <p className='pHeader'>Select LAS Folder:</p>
        <div className="DF">
          <button className='toggle-btn-las' type="button" onClick={() => setShowFolderTreePopup(true)}>
            <p>Select LAS Folder(s)</p>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V9C21 7.89543 20.1046 7 19 7H12L10 5H5C3.89543 5 3 5.89543 3 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 14L12 11L15 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Render the Folder Tree Popup Modal */}
        {showFolderTreePopup && (
          <FolderTreePopup 
            onClose={() => setShowFolderTreePopup(false)}
            onFolderSelect={(folders) => setSelectedFolders(Array.isArray(folders) ? folders : [folders])}
            onDriveSelect={(driveName) => setSelectedDriveName(driveName)}
            storageConnection={selectedConnection}
            useSystemDrive={useSystemGoogleDrive}
          />
        )}

        {/* Storage Connection Modal */}
        {showConnectionModal && connectionProvider && (
          <StorageConnectionModal
            onClose={() => {
              setShowConnectionModal(false);
              setConnectionProvider(null);
            }}
            provider={connectionProvider}
            onConnectionSuccess={() => {
              // Reload connections after successful connection
              loadStorageConnections();
            }}
          />
        )}

       {/* Render the Clear Confirmation Popup Modal */}
       {showClearPopup && (
         <div className="TreeModalOverlay">
           <div className="TreeModalContainer">
             <div className="modal-header">
               <h2 className="modal-title">Clear Form</h2>
             </div>
             
             <div className="modal-content">
               <p className="clear-popup-message">
                 Are you sure you want to completely clear the page? This action cannot be undone.
               </p>
             </div>

             <div className="modal-actions">
               <button className="modal-btn modal-btn-secondary" onClick={handleClearCancel}>
                 Cancel
               </button>
               <button className="modal-btn modal-btn-primary" onClick={handleClearConfirm}>
                 Yes, Clear All
               </button>
             </div>
           </div>
         </div>
       )}
            </div>
          </div>
          
          {/* Setup Tab Panel */}
          <div className={`main-content-tab-panel main-content-tab-panel-setup ${activeMainTab === 'setup' ? 'active' : ''}`}>
            <div className="tab-content-RSA">
              {/* Cloud Storage Service Selector */}
              <PermissionGate group="Admin" fallback={
                <div style={{ padding: '10px', background: '#fee', border: '1px solid #fcc', margin: '10px 0' }}>
                  <p style={{ margin: 0, color: '#c00' }}>
                    ⚠️ Storage Service Selector is only available to Admin users.
                    <br />
                    <small>Check browser console for permission details.</small>
                  </p>
                </div>
              }>
                <div className='storage-service-selector'>
                  <p className='pHeader'>Select your Cloud Storage Service:</p>
                  <div className="storage-providers-container">
                  {/* System Google Drive - Uses existing service account integration */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 'system_google_drive' ? 'selected' : ''}`}
                    type="button"
                    onClick={() => {
                      setSelectedStorageProvider('system_google_drive');
                      setUseSystemGoogleDrive(true);
                      setSelectedConnection(null);
                    }}
                    title="System Google Drive (Service Account)"
                  >
                    <GoogleDriveIcon className="storage-provider-icon" style={{ width: '20px', height: '20px' }} />
                    <span>System Drive</span>
                  </button>

                  {/* User's Google Drive - Uses OAuth 2.0 */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 'google_drive' ? 'selected' : ''} ${!hasGoogleDriveConnection ? 'needs-connection' : ''}`}
                    type="button"
                    onClick={() => {
                      const googleDriveConn = storageConnections.find(c => c.provider_type === 'google_drive' && c.is_active);
                      if (googleDriveConn) {
                        setSelectedStorageProvider('google_drive');
                        setSelectedConnection(googleDriveConn);
                        setUseSystemGoogleDrive(false);
                      } else {
                        setConnectionProvider('google_drive');
                        setShowConnectionModal(true);
                      }
                    }}
                    title={!hasGoogleDriveConnection ? 'Click to connect your Google Drive via OAuth 2.0' : 'Your Google Drive (OAuth 2.0)'}
                  >
                    <GoogleDriveIcon className="storage-provider-icon" style={{ width: '18px', height: '18px' }} />
                    <span>Google Drive</span>
                    {!hasGoogleDriveConnection && (
                      <span className="connection-indicator">
                        <i className="fa-solid fa-plus"></i>
                      </span>
                    )}
                  </button>

                  {/* AWS S3 - Uses Access Keys (NOT OAuth) */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 's3' ? 'selected' : ''} ${!hasS3Connection ? 'needs-connection' : ''}`}
                    type="button"
                    onClick={() => {
                      const s3Conn = storageConnections.find(c => c.provider_type === 's3' && c.is_active);
                      if (s3Conn) {
                        setSelectedStorageProvider('s3');
                        setSelectedConnection(s3Conn);
                        setUseSystemGoogleDrive(false);
                      } else {
                        setConnectionProvider('s3');
                        setShowConnectionModal(true);
                      }
                    }}
                    title={!hasS3Connection ? 'Click to connect S3 with Access Keys' : 'AWS S3 (Access Keys)'}
                  >
                    <AmazonS3Icon className="storage-provider-icon" style={{ width: '20px', height: '20px' }} />
                    <span>AWS S3</span>
                    {!hasS3Connection && (
                      <span className="connection-indicator">
                        <i className="fa-solid fa-plus"></i>
                      </span>
                    )}
                  </button>

                  {/* Azure Blob Storage - Uses OAuth 2.0 */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 'azure' ? 'selected' : ''} ${!hasAzureConnection ? 'needs-connection' : ''}`}
                    type="button"
                    onClick={() => {
                      const azureConn = storageConnections.find(c => c.provider_type === 'azure' && c.is_active);
                      if (azureConn) {
                        setSelectedStorageProvider('azure');
                        setSelectedConnection(azureConn);
                        setUseSystemGoogleDrive(false);
                      } else {
                        setConnectionProvider('azure');
                        setShowConnectionModal(true);
                      }
                    }}
                    title={!hasAzureConnection ? 'Click to connect Azure via OAuth 2.0' : 'Azure (OAuth 2.0)'}
                  >
                    <AzureIcon className="storage-provider-icon" style={{ width: '20px', height: '20px' }} />
                    <span>Microsoft Azure</span>
                    {!hasAzureConnection && (
                      <span className="connection-indicator">
                        <i className="fa-solid fa-plus"></i>
                      </span>
                    )}
                  </button>

                  {/* Google Cloud Storage - Uses OAuth 2.0 or Service Account */}
                  <button 
                    className={`toggle-btn-rsa task-button storage-provider-btn ${selectedStorageProvider === 'gcp' ? 'selected' : ''} ${!hasGCPConnection ? 'needs-connection' : ''}`}
                    type="button"
                    onClick={() => {
                      const gcpConn = storageConnections.find(c => c.provider_type === 'gcp' && c.is_active);
                      if (gcpConn) {
                        setSelectedStorageProvider('gcp');
                        setSelectedConnection(gcpConn);
                        setUseSystemGoogleDrive(false);
                      } else {
                        setConnectionProvider('gcp');
                        setShowConnectionModal(true);
                      }
                    }}
                    title={!hasGCPConnection ? 'Click to connect GCP via OAuth 2.0 or Service Account' : 'GCP (OAuth 2.0)'}
                  >
                    <GoogleCloudIcon className="storage-provider-icon" style={{ width: '22px', height: '22px' }} />
                    <span>Google Cloud Storage</span>
                    {!hasGCPConnection && (
                      <span className="connection-indicator">
                        <i className="fa-solid fa-plus"></i>
                      </span>
                    )}
                  </button>
                </div>
                </div>
              </PermissionGate>
            </div>
          </div>
          
          {/* Paths Tab Panel */}
          <div className={`main-content-tab-panel main-content-tab-panel-paths ${activeMainTab === 'paths' ? 'active' : ''}`}>
            <div className="tab-content-RSA">
              {/* Paths content will go here */}
            </div>
          </div>
          
          {/* Dashboard Tab Panel */}
          <div className={`main-content-tab-panel main-content-tab-panel-dashboard ${activeMainTab === 'dashboard' ? 'active' : ''}`}>
            <div className="tab-content-RSA">
              <AnalysisDashboard 
                selectedRun={dashboardSelectedRun}
                setSelectedRun={setDashboardSelectedRun}
                mockProjectsData={dashboardProjectsData}
                displayRuns={displayRuns}
              />
            </div>
          </div>
          
          {/* Settings Tab Panel */}
          <div className={`main-content-tab-panel main-content-tab-panel-settings ${activeMainTab === 'settings' ? 'active' : ''}`}>
            <div className="tab-content-RSA">
              {/* Settings content will go here */}
            </div>
          </div>
        </div>
      </div>
      {/* Right side panels - Summary and TabsRSA side by side */}
      <div className="right-panels">
        {activeMainTab === 'dashboard' ? (
          /* Dashboard Projects Panel */
          <DashboardProjectsPanel
            projects={dashboardProjectsData}
            runs={displayRuns}
            selectedRun={dashboardSelectedRun}
            onSelectRun={setDashboardSelectedRun}
            onDeleteProject={handleDeleteProject}
          />
        ) : (
          <>
            {/* Summary Component - Always visible on the right side */}
            {/* Horizontal/Embankment Tabs - Side by side with summary */}
            {(services.includes('horizontal') || services.includes('embankment')) && (
              <TabsRSA
                services={services}
                lidarType={lidarType}
                onSaveMap={{
                  horizontal: setHorizontalData,
                  embankment: setEmbankmentData
                }}
              />
            )}
            <RSASummary
              existingProjectName={existingProjectName}
              selectedProjectId={selectedProjectId}
              services={services}
              selectedFolders={selectedFolders}
              selectedDriveName={selectedDriveName}
              roadDetectionCategory={roadDetectionCategory}
              horizontalData={horizontalData}
              EmbankmentData={EmbankmentData}
              selectedStorageProvider={selectedStorageProvider}
              onSubmit={handleSubmit}
              onClear={handleClearClick}
            />
          </>
        )}
      </div>
      
      
    </div>
  );
}

export default PayloadForm;
